import { CartAddEvent } from '@theme/events';

/**
 * Injects the loading-overlay styles once, so no liquid/CSS edits are
 * needed in aegis-purchase-widget.liquid.
 */
function injectOverlayStyles() {
  if (document.getElementById('apw-overlay-styles')) return;

  const style = document.createElement('style');
  style.id = 'apw-overlay-styles';
  style.textContent = `
    .apw-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      opacity: 0;
      transition: opacity 0.15s ease;
    }
    .apw-overlay[data-visible="true"] {
      opacity: 1;
    }
    .apw-overlay__spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.25);
      border-top-color: #C6FF00;
      border-radius: 50%;
      animation: apw-spin 0.6s linear infinite;
    }
    @keyframes apw-spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

/**
 * Shows a full-screen loading overlay and returns a function that hides it.
 */
function showOverlay() {
  injectOverlayStyles();

  const overlay = document.createElement('div');
  overlay.className = 'apw-overlay';

  const spinner = document.createElement('div');
  spinner.className = 'apw-overlay__spinner';
  overlay.appendChild(spinner);

  document.body.appendChild(overlay);
  // Force a frame so the opacity transition actually runs
  requestAnimationFrame(() => overlay.setAttribute('data-visible', 'true'));

  return () => {
    overlay.removeAttribute('data-visible');
    overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
    // Fallback in case transitionend doesn't fire
    setTimeout(() => overlay.remove(), 300);
  };
}

/**
 * Intercepts submits on the custom Aegis purchase widget forms
 * (subscribe + one-time-purchase) and adds to cart via AJAX instead
 * of a native form POST — which is what was causing the redirect to
 * /cart. On success it dispatches the same CartAddEvent that Horizon's
 * product-form-component dispatches, so the existing cart-drawer-component
 * (already listening on `document`) opens automatically and the cart
 * contents update via the normal theme section-rendering flow.
 */
document.addEventListener('submit', (event) => {
  const form = event.target;

  if (!(form instanceof HTMLFormElement) || !form.matches('[data-aegis-atc-form]')) return;

  event.preventDefault();

  const submitter = event.submitter;
  if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
    submitter.disabled = true;
  }

  const hideOverlay = showOverlay();

  const formData = new FormData(form);

  // Request updated section HTML for any cart-items-component on the page,
  // same as Horizon's product-form-component does, so the drawer content
  // (cart-products, cart-summary, cart-bubble) refreshes without a reload.
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  cartItemsComponents.forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
  });
  if (sectionIds.length) {
    formData.append('sections', sectionIds.join(','));
  }

  const cartAddUrl =
    (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add';

  fetch(cartAddUrl, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: formData,
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        // Shopify returned an error (e.g. sold out, invalid variant)
        console.error('Aegis purchase widget add-to-cart error:', response.message);
        return;
      }

      const id = formData.get('id');

      // This is the event cart-drawer-component listens for on `document`
      // to open itself, and that cart-items-component listens for to
      // refresh its contents from response.sections.
      document.dispatchEvent(
        new CartAddEvent({}, id ? id.toString() : '', {
          source: 'aegis-purchase-widget',
          itemCount: 1,
          productId: form.querySelector('input[name="id"]')?.value,
          sections: response.sections,
        })
      );
    })
    .catch((error) => {
      console.error('Aegis purchase widget add-to-cart failed:', error);
    })
    .finally(() => {
      if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
        submitter.disabled = false;
      }
      hideOverlay();
    });
});